<template>
  <div class="product-page">
    <block-buttons
      title="Товары"
      description="Каталог, цены и остатки — в одном месте"
      :actions="toolbarActions"
    >
      <template #icon>
        <Package :size="22"/>
      </template>
    </block-buttons>

    <product-summary
      v-if="!isSeller"
      :products="products"
      :unavailable="loading || Boolean(error)"
    />

    <div class="product-page__filters">
      <label>Наличие
        <select v-model="stock">
          <option value="">Все товары</option>
          <option value="available">В наличии</option>
          <option value="empty">Нет в наличии</option>
          <option value="sale">Со скидкой</option>
        </select>
      </label>
    </div>

    <data-table
      :rows="filteredRows"
      :columns="tableColumns"
      :loading="loading"
      :error="error"
      title="Каталог товаров"
      description="Цены, скидки и остатки товаров"
      :search-keys="['title', 'code', 'categoryName']"
      search-placeholder="Название, код или категория"
      empty-text="Товаров с выбранными фильтрами нет"
      @retry="loadDataWrapper(true)"
    >
      <template #filters>
        <slot name="filters" />
      </template>

      <template #header>
        <span class="product-table__badge">Позиций: {{ filteredRows.length }}</span>
      </template>

      <template #cell-title="{ row }">
        <strong>{{ getTitle(row) }}</strong>
        <small>Код: {{ getCode(row) }}</small>

        <span
          v-if="Number(row.salePrice) > 0"
          class="product-table__badge product-table__badge--sale"
        >
          Скидка
        </span>
      </template>

      <template #cell-price="{ row }">
        <strong>{{ getPrice(row) }}</strong>
        <del v-if="Number(row.salePrice) > 0">{{ priceToFormat(row.price) }}</del>
      </template>

      <template #cell-salePrice="{ row }">{{ getSalePrice(row) }}</template>

      <template #cell-purchasePrice="{ row }">
        <span>{{ priceToFormat(row.purchasePrice) }}</span>
      </template>

      <template #cell-count="{ row }">
        <span
          class="product-table__badge"
          :class="getClassBadge(row)"
        >
          {{ getBadge(row) }}
        </span>
      </template>

      <template #cell-actions="{ row }">
        <select
          class="product-table__actions"
          :disabled="loading"
          :aria-label="`Действия с товаром ${row.title}`"
          @change="selectAction($event, row)"
        >
          <option value="">Действия</option>

          <option
            v-for="action in rowActions"
            :key="action.id"
            :value="action.id"
          >
            {{ action.label }}
          </option>
        </select>
      </template>
    </data-table>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { Package, Plus, RefreshCw } from 'lucide-vue'

import BlockButtons from '@/components/blockButtons'
import ProductSummary from '@/components/productSummary'
import permissions from '@/mixins/permissions'

import DataTable from '@/components/dataTable'

import { priceToFormat } from '@/helper'

export default {
  name: 'ProductV2',
  components: {
    DataTable,
    Package,
    BlockButtons,
    ProductSummary
  },
  mixins: [permissions],
  data () {
    return {
      loading: false,
      error: '',
      stock: ''
    }
  },
  computed: {
    ...mapGetters('product', { products: 'getData' }),
    ...mapGetters('settings', ['getTableColumns']),
    ...mapGetters('config', ['getIsOffline']),
    ...mapGetters('user', ['isSeller']),
    toolbarActions () {
      const actions = [
        {
          id: 'refresh',
          label: 'Обновить',
          icon: RefreshCw,
          disabled: this.loading,
          handler: () => this.loadDataWrapper(true)
        }
      ]

      if (this.isAllowedOperation('create', 'product')) {
        actions.push({
          id: 'create',
          label: 'Новый товар',
          icon: Plus,
          primary: true,
          disabled: this.loading || this.getIsOffline,
          handler: () => {
            this.openModal({
              name: 'create-base',
              propsData: {
                name: 'product',
                moduleAction: 'product',
                type: 'create',
                title: 'Добавление нового товара',
                text: 'Укажите данные товара',
                textCancelBtn: 'Закрыть',
                textConfirmBtn: 'Добавить товар'
              }
            })
          }
        })
      }

      return actions
    },
    tableColumns () {
      return [
        {
          key: 'actions',
          label: 'Действия',
          sortable: false
        },
        ...(this.getTableColumns?.['product'] || [])
      ]
    },
    filteredRows () {
      return this.products.filter(({ count, salePrice }) => {
        if (this.stock === 'available') {
          return Number(count) > 0
        }

        if (this.stock === 'empty') {
          return count != null && Number(count) <= 0
        }

        if (this.stock === 'sale') {
          return Number(salePrice) > 0
        }

        return true
      })
    },
    rowActions () {
      return [
        {
          id: 'info',
          label: 'Информация',
          visible: true
        },
        {
          id: 'change',
          label: 'Редактировать',
          visible: this.isAllowedOperation('change', 'product')
        },
        {
          id: 'remove',
          label: 'Удалить',
          visible: this.isAllowedOperation('remove', 'product')
        }
      ].filter(({ visible }) => visible)
    }
  },
  mounted () {
    this.loadDataWrapper()
  },
  methods: {
    ...mapActions('product', ['loadData', 'remove']),
    ...mapActions('modal', ['openModal']),
    priceToFormat,
    getTitle (row) {
      return row.title || 'Без названия'
    },
    getCode (row) {
      return row.code || '—'
    },
    getPrice (row) {
      return priceToFormat(Number(row.salePrice) > 0 ? row.salePrice : row.price)
    },
    getSalePrice (row) {
      return Number(row.salePrice) > 0 ? priceToFormat(row.salePrice) : '—'
    },
    getClassBadge (row) {
      return { 'product-table__badge--empty': Number(row.count) <= 0 }
    },
    getBadge (row) {
      return row.count == null ? 'Не указан' : Number(row.count) > 0 ? row.count + ' шт.' : 'Нет в наличии'
    },
    selectAction (event, row) {
      const action = event.target.value

      event.target.value = ''

      if (!action) {
        return
      }

      this.openAction(action, row)
    },
    async loadDataWrapper (force = false) {
      if (this.loading) {
        return
      }

      this.$bus.emit('start-loader')
      this.loading = true

      await this.loadData({ force })

      this.loading = false
      this.$bus.emit('stop-loader')
    },
    openAction (action, row) {
      switch (action) {
        case 'info':
          this.openModal({
            name: 'info-base',
            propsData: {
              name: 'product',
              moduleAction: 'product',
              type: 'info',
              title: `Информация - ${row.title}`,
              textCancelBtn: 'Закрыть',
              data: row
            }
          })

          break
        case 'change':
          this.openModal({
            name: 'change-base',
            propsData: {
              name: 'product',
              moduleAction: 'product',
              type: 'change',
              title: `Редактирование - ${row.title}`,
              text: 'Измените необходимые данные',
              textCancelBtn: 'Закрыть',
              textConfirmBtn: 'Сохранить',
              data: row
            }
          })

          break
        case 'remove':
          this.removeProduct(row)
      }
    },
    async removeProduct ({ id }) {
      const { error } = await this.remove(id)

      if (error) {
        this.$notify({
          type: 'error',
          title: 'Ошибка при удалении!',
          text: 'Произошла ошибка при удалении товара!',
          clean: false,
          duration: 3000
        })

        return
      }

      this.$notify({
        type: 'success',
        title: 'Товар успешно удален!',
        text: '',
        clean: false,
        duration: 3000
      })
    }
  }
}
</script>

<style scoped lang="scss" src="./style.scss"/>
